import { z } from 'zod';

const jsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);

const jsonObjectSchema = z.record(jsonValueSchema);
const emptyInputSchema = z.object({}).strict();
const searchAfterSchema = z.array(z.union([z.string(), z.number(), z.null()]));
const stringListSchema = z.array(z.string());

/**
 * The legacy query filters are intentionally explicit at the request boundary:
 * an unknown top-level filter is rejected rather than silently changing a query.
 * Values remain transport-shaped because individual query families coerce their
 * numeric values differently (for example payouts cents versus transaction dollars).
 */
export const searchReportingFiltersSchema = z
  .object({
    merchantNames: stringListSchema.optional(),
    amountEquals: z.union([z.string(), z.number()]).optional(),
    amountGreaterThan: z.union([z.string(), z.number()]).optional(),
    amountLessThan: z.union([z.string(), z.number()]).optional(),
    amountType: z.enum(['AMOUNT', 'NET_AMOUNT', 'CHARGED_AMOUNT', 'TOTAL_AMOUNT']).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    dateType: z
      .enum([
        'CREATED_DATE',
        'LAST_CHARGE_DATE',
        'NEXT_CHARGE_DATE',
        'END_CHARGE_DATE',
        'ARRIVAL_DATE',
      ])
      .optional(),
    paymentMethods: stringListSchema.optional(),
    paymentTypes: stringListSchema.optional(),
    states: stringListSchema.optional(),
    statuses: stringListSchema.optional(),
    types: stringListSchema.optional(),
    formNames: stringListSchema.optional(),
    formTypes: stringListSchema.optional(),
    fundNames: stringListSchema.optional(),
    ticketNames: stringListSchema.optional(),
    confirmationIDs: stringListSchema.optional(),
    attendeeConfirmationIDs: stringListSchema.optional(),
    attendeeNames: stringListSchema.optional(),
    names: stringListSchema.optional(),
    contactIDs: stringListSchema.optional(),
    emails: stringListSchema.optional(),
    frequencies: stringListSchema.optional(),
    transactionIDs: stringListSchema.optional(),
    subscriptionIDs: stringListSchema.optional(),
    attributedBroadcastIds: stringListSchema.optional(),
    attributedBroadcastId: z.string().optional(),
    broadcastTypes: stringListSchema.optional(),
    broadcastChannels: stringListSchema.optional(),
    ids: stringListSchema.optional(),
    selectedIds: stringListSchema.optional(),
    excludedIds: stringListSchema.optional(),
    keyword: z.string().optional(),
    contactID: z.string().optional(),
    payoutID: z.string().optional(),
    merchantTimezone: z.string().optional(),
    timezone: z.string().optional(),
    // CON-857: the dashboard sends the legacy list vocabulary (['taxDeductible'],
    // ['nonTaxDeductible']); other words are rejected so a typo cannot silently drop the filter.
    taxDeductible: z.union([
      z.boolean(),
      z.array(z.union([z.boolean(), z.enum(['taxDeductible', 'nonTaxDeductible'])])),
    ]).optional(),
    limit: z.number().int().positive().optional(),
    searchAfter: searchAfterSchema.optional(),
    sortField: z.string().optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
    sortKey: z.string().optional(),
    sourceFields: z.array(z.string().min(1)).max(50).optional(),
    query: z.string().optional(),
    memberships: stringListSchema.optional(),
    membershipIds: stringListSchema.optional(),
    tags: stringListSchema.optional(),
    hasAllTags: stringListSchema.optional(),
    missingTags: stringListSchema.optional(),
    countries: stringListSchema.optional(),
    cities: stringListSchema.optional(),
    emailConsent: stringListSchema.optional(),
    smsConsent: stringListSchema.optional(),
    donorStatus: z.string().optional(),
    years: z.array(z.union([z.string(), z.number()])).optional(),
    donationCountGte: z.union([z.string(), z.number()]).optional(),
    donationCountLte: z.union([z.string(), z.number()]).optional(),
    periodAmountGte: z.union([z.string(), z.number()]).optional(),
    periodAmountLte: z.union([z.string(), z.number()]).optional(),
    elementNames: stringListSchema.optional(),
    syncErrorTypes: stringListSchema.optional(),
    name_search: z.string().optional(),
    broadcast_types: stringListSchema.optional(),
    channels: stringListSchema.optional(),
    segment_ids: stringListSchema.optional(),
    sender_emails: stringListSchema.optional(),
    start_date: z.string().optional(),
    end_date: z.string().optional(),
    amount_greater_than: z.union([z.string(), z.number()]).optional(),
    amount_less_than: z.union([z.string(), z.number()]).optional(),
    sent_count_greater_than: z.union([z.string(), z.number()]).optional(),
    sent_count_less_than: z.union([z.string(), z.number()]).optional(),
    sort_by: z.enum(['pf_updated_at', 'attributed_revenue']).optional(),
    sort_order: z.enum(['asc', 'desc']).optional(),
    debug: z.boolean().optional(),
    debugScript: z.boolean().optional(),
  })
  .strict();

const queryInputSchema = z
  .object({ query: z.string(), filters: searchReportingFiltersSchema })
  .strict();
const checkInCountsInputSchema = z
  .object({
    query: z.literal('checkInCounts'),
    filters: searchReportingFiltersSchema,
    events: z
      .array(
        z
          .object({
            routeId: z.string(),
            formID: z.string().optional(),
            elementID: z.string().optional(),
            formName: z.string().optional(),
          })
          .strict(),
      )
      .min(1)
      .max(100),
  })
  .strict();
const requireOne = <T extends z.ZodRawShape>(shape: T, keys: readonly (keyof T)[]) =>
  z
    .object(shape)
    .strict()
    .refine((value) => keys.some((key) => value[key] !== undefined), {
      message: `one of ${keys.join(', ')} is required`,
    });
const validateTransactionInputSchema = requireOne(
  { transactionID: z.string().optional(), contactID: z.string().optional() },
  ['transactionID', 'contactID'],
);
const validateContactInputSchema = requireOne(
  { contactID: z.string().optional(), email: z.string().optional() },
  ['contactID', 'email'],
);
const validateSubscriptionsInputSchema = requireOne(
  {
    subscriptionID: z.string().optional(),
    subscriptionIDs: z.array(z.string()).optional(),
    email: z.string().optional(),
    contactID: z.string().optional(),
  },
  ['subscriptionID', 'subscriptionIDs', 'email', 'contactID'],
);
const validateFormInputSchema = requireOne(
  { formID: z.string().optional(), formIDs: z.array(z.string()).optional() },
  ['formID', 'formIDs'],
);
const validatePayoutInputSchema = z.object({ payoutID: z.string() }).strict();
const validateElementInputSchema = z.object({ elementID: z.string() }).strict();
const loginInputSchema = z
  .object({
    start: z.string().optional(),
    end: z.string().optional(),
    cursor: z.string().optional(),
  })
  .strict();
const selectionSummaryInputSchema = z
  .object({
    entityType: z.enum([
      'transactions',
      'contacts',
      'subscriptions',
      'payouts',
      'payouts-drilldown',
      'fundraiser-drilldown',
      'event-drilldown',
      'forms',
      'elements',
    ]),
    filters: searchReportingFiltersSchema,
    excludedIds: z.array(z.string()).optional(),
  })
  .strict();
const donationYearsInputSchema = z
  .object({
    filters: searchReportingFiltersSchema,
    selectedIds: z.array(z.string()).optional(),
    excludedIds: z.array(z.string()).optional(),
  })
  .strict();

const listOutputSchema = z.array(jsonValueSchema);
const checkInCountsOutputSchema = z
  .object({
    contractVersion: z.literal(1),
    asOf: z.string(),
    counts: z.record(
      z.string(),
      z
        .object({
          checkedInCount: z.number().int().nullable(),
          complete: z.boolean(),
          identitySource: z.enum(['formID', 'elementID', 'none']),
          reason: z.enum(['unverifiable_form_name', 'missing_aggregation']).optional(),
        })
        .strict(),
    ),
  })
  .strict();
const validationOutputSchema = <K extends string>(key: K, value: z.ZodTypeAny) =>
  z.object({ isValid: z.boolean(), [key]: value }).strict();
const transactionValidationOutputSchema = validationOutputSchema(
  'transactionInformation',
  z.array(jsonObjectSchema),
);
const contactConsentOverlaySchema = z
  .object({
    emailConsent: z.unknown(),
    smsConsent: z.unknown(),
    communicationConsent: z.unknown(),
    hasBroadcastOptOut: z.boolean(),
  })
  .partial({ emailConsent: true, smsConsent: true, communicationConsent: true });
const contactValidationOutputSchema = validationOutputSchema(
  'contactInformation',
  z.union([z.null(), z.intersection(jsonObjectSchema, contactConsentOverlaySchema)]),
);
const subscriptionsValidationOutputSchema = validationOutputSchema(
  'subscriptionsInformation',
  z.array(jsonObjectSchema),
);
const formValidationOutputSchema = validationOutputSchema(
  'formInformation',
  z.array(jsonObjectSchema),
);
const payoutValidationOutputSchema = validationOutputSchema(
  'payoutInformation',
  z.union([z.null(), jsonObjectSchema]),
);
const elementValidationOutputSchema = validationOutputSchema(
  'elementInformation',
  z.union([z.null(), jsonObjectSchema]),
);
const selectionSummaryOutputSchema = z
  .object({
    totalCount: z.number(),
    withEmail: z.number().optional(),
    taxDeductible: z.number().optional(),
    nonTaxDeductible: z.number().optional(),
    offlinePayments: z.number().optional(),
    activeCount: z.number().optional(),
    scheduledCount: z.number().optional(),
    canceledCount: z.number().optional(),
    inactiveCount: z.number().optional(),
    deletableElementCount: z.number().optional(),
  })
  .strict();
const donationYearsOutputSchema = z
  .object({
    totalCount: z.number(),
    years: z.array(z.object({
      year: z.number(),
      contactCount: z.number(),
      currencies: z.array(z.string().regex(/^[A-Z]{3}$/)).optional(),
      // CON-857: distinct contacts per currency; only with currencies, ambiguous codes omitted.
      contactCountsByCurrency: z.record(z.string().regex(/^[A-Z]{3}$/), z.number().int().nonnegative()).optional(),
    }).strict()),
  })
  .strict();
const transactionChartDateSchema = z
  .string()
  .regex(/^\d{2}-\d{2}-\d{4}$/)
  .refine((value) => {
    const month = Number(value.slice(0, 2));
    const day = Number(value.slice(3, 5));
    const year = Number(value.slice(6));
    const leapYear = year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
    const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return month >= 1 && month <= 12 && day >= 1 && day <= (daysInMonth[month - 1] ?? 0);
  });
const transactionChartOutputSchema = z.record(transactionChartDateSchema, z.number());
const contactsYearlyDonationsOutputSchema = z.record(
  z
    .object({
      years: z.array(z.number().int()),
      yearTotals: z.record(z.number()),
    })
    .strict(),
);
const contactGeoStateSchema = z
  .object({
    value: z.string(),
    cities: z.array(z.string()),
  })
  .strict();
const contactGeoOptionsOutputSchema = z
  .object({
    countries: z.array(
      z
        .object({
          value: z.string(),
          states: z.array(contactGeoStateSchema),
          cities: z.array(z.string()),
        })
        .strict(),
    ),
    allStates: z.array(contactGeoStateSchema),
    allCities: z.array(z.string()),
  })
  .strict();
const notificationKeys = [
  'id',
  'actionType',
  'entityType',
  'status',
  'actionLabel',
  'userEmail',
  'clerkUserID',
  'isRead',
  'createdAt',
  'metadata',
] as const;
const notificationSchema = z
  .object(
    Object.fromEntries(notificationKeys.map((key) => [key, z.unknown()])) as {
      [K in (typeof notificationKeys)[number]]: z.ZodUnknown;
    },
  )
  .strict()
  .superRefine((value, context) => {
    for (const key of notificationKeys)
      if (!Object.prototype.hasOwnProperty.call(value, key))
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `${key} is required`,
        });
  });
const notificationsOutputSchema = z
  .object({ notifications: z.array(notificationSchema).max(50) })
  .strict();
const loginCardSchema = z
  .object({
    portal_widget: z.number(),
    portal_page: z.number(),
    unknown: z.number(),
    total: z.number(),
  })
  .strict();
const loginsOutputSchema = z
  .object({
    range: z.object({ start: z.string().nullable(), end: z.string().nullable() }).strict(),
    cards: z
      .object({
        uniqueDonors: loginCardSchema,
        totalLogins: loginCardSchema,
        signups: loginCardSchema,
      })
      .strict(),
    drilldown: z
      .object({
        items: z.array(
          z
            .object({
              contactID: z.string().nullable(),
              name: z.string(),
              email: z.string(),
              lastLogin: z.string().nullable(),
              source: z.string().nullable(),
              isSignup: z.boolean(),
            })
            .strict(),
        ),
        nextCursor: z.number().nullable(),
      })
      .strict(),
    backfillNote: z.object({ historicalCutoff: z.string().nullable() }).strict(),
  })
  .strict();

export const searchReportingRouteSchemas = {
  'POST /transactions': {
    input: queryInputSchema,
    output: z.union([
      listOutputSchema,
      transactionChartOutputSchema,
      contactsYearlyDonationsOutputSchema,
    ]),
  },
  'POST /contacts': {
    input: queryInputSchema,
    output: z.union([listOutputSchema, contactGeoOptionsOutputSchema]),
  },
  'POST /subscriptions': { input: queryInputSchema, output: listOutputSchema },
  'POST /forms': { input: queryInputSchema, output: listOutputSchema },
  'POST /elements': { input: queryInputSchema, output: listOutputSchema },
  'POST /fundraiser-drilldown': { input: queryInputSchema, output: listOutputSchema },
  'POST /event-drilldown': {
    input: z.union([checkInCountsInputSchema, queryInputSchema]),
    output: z.union([checkInCountsOutputSchema, listOutputSchema]),
  },
  'POST /payouts': { input: queryInputSchema, output: listOutputSchema },
  'POST /payouts-drilldown': { input: queryInputSchema, output: listOutputSchema },
  'POST /broadcasts': { input: queryInputSchema, output: listOutputSchema },
  'POST /segments': { input: queryInputSchema, output: listOutputSchema },
  'POST /top': { input: queryInputSchema, output: listOutputSchema },
  'GET /validate/transaction': {
    input: validateTransactionInputSchema,
    output: transactionValidationOutputSchema,
  },
  'GET /validate/contact': {
    input: validateContactInputSchema,
    output: contactValidationOutputSchema,
  },
  'GET /validate/subscriptions': {
    input: validateSubscriptionsInputSchema,
    output: subscriptionsValidationOutputSchema,
  },
  'GET /validate/form': {
    input: validateFormInputSchema,
    output: formValidationOutputSchema,
  },
  'GET /validate/payout': {
    input: validatePayoutInputSchema,
    output: payoutValidationOutputSchema,
  },
  'GET /validate/element': {
    input: validateElementInputSchema,
    output: elementValidationOutputSchema,
  },
  'POST /selection-summary': {
    input: selectionSummaryInputSchema,
    output: selectionSummaryOutputSchema,
  },
  'POST /contacts/donation-years': {
    input: donationYearsInputSchema,
    output: donationYearsOutputSchema,
  },
  'GET /notifications': { input: emptyInputSchema, output: notificationsOutputSchema },
  'GET /analytics/logins': { input: loginInputSchema, output: loginsOutputSchema },
} as const;

export function searchReportingOutputSchemaForQuery(
  route: SearchReportingRoute,
  query: unknown,
) {
  if (route === 'POST /transactions') {
    if (query === 'chart') return transactionChartOutputSchema;
    if (query === 'contactsYearlyDonations') return contactsYearlyDonationsOutputSchema;
    return listOutputSchema;
  }
  if (route === 'POST /contacts')
    return query === 'contactGeoOptions' ? contactGeoOptionsOutputSchema : listOutputSchema;
  return searchReportingRouteSchemas[route].output;
}

export const searchReportingStreamRecordSchema = z
  .object({
    eventName: z.enum(['INSERT', 'MODIFY', 'REMOVE']),
    eventSourceARN: z.string(),
    sequenceNumber: z.string().optional(),
    keys: z.record(z.object({ S: z.string() }).strict()),
    newImage: jsonObjectSchema.optional(),
    oldImage: jsonObjectSchema.optional(),
  })
  .strict();

export const searchReportingStreamResultSchema = z
  .object({
    stream: z.string(),
    index: z.string(),
    documentId: z.string(),
    operation: z.enum(['PUT', 'DELETE']),
    document: jsonObjectSchema.optional(),
  })
  .strict();

export type SearchReportingRoute = keyof typeof searchReportingRouteSchemas;
export type SearchReportingRouteInput<R extends SearchReportingRoute> = z.input<
  (typeof searchReportingRouteSchemas)[R]['input']
>;
export type SearchReportingRouteOutput<R extends SearchReportingRoute> = z.output<
  (typeof searchReportingRouteSchemas)[R]['output']
>;
export type SearchReportingStreamRecord = z.infer<typeof searchReportingStreamRecordSchema>;
export type SearchReportingStreamResult = z.infer<typeof searchReportingStreamResultSchema>;
